import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import CONFIG from "../../config";
import { useAuth } from "../../context/AuthProvider";
import { usePatient } from "../../context/PatientContext";
import { useTheme } from "../../context/ThemeContext";
import TransliteratedInput from "../common/TransliteratedInput";

const AddClinic = () => {
  const [formData, setFormData] = useState({
    CLINIC_NAME: "",
    CLINIC_ADDRESS: "",
    CLINIC_PHONE: "",
  });

  const { theme, isDarkMode } = useTheme();
  const token = localStorage.getItem("authToken");
  const { verifyTokenAndRedirect } = useAuth();
  const navigate = useNavigate();
  const baseURL = CONFIG.BASE_URL;
  const { doctorInfo } = usePatient();

  useEffect(() => {
    verifyTokenAndRedirect();
  }, []);

  // Handle form input changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Submit form data to the backend
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      console.log("sending the request to backend");

      await axios.post(`${baseURL}/doctors/addclinic`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          doctorId: doctorInfo.MANDALI_ID,
          clinicId: doctorInfo.CLINIC_ID,
          roleId: doctorInfo.MANDALI_ROLE
        }
      });
      alert("Clinic Added successfully");
      navigate("/doctorslist");
    } catch (error) {
      console.error("Error adding Clinic:", error);
      alert("Failed to add Clinic. Please try again.");
    }
  };

  return (
    <div
      className="min-h-screen w-full transition-colors duration-700 ease-in-out flex flex-col items-center justify-center p-4 md:p-8"
      style={{
        background: 'transparent',
        color: theme.text
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="w-full max-w-4xl rounded-[2.5rem] shadow-[0_50px_100px_-20px_rgba(0,0,0,0.3)] transition-all duration-500 overflow-hidden"
        style={{
          backgroundColor: theme.card,
        }}
      >
        {/* Header Section */}
        <div className="px-8 md:px-12 pt-8 md:pt-10 pb-6 border-b" style={{ borderColor: theme.border }}>
          <h2
            className="text-2xl md:text-3xl mb-2 tracking-tight"
            style={{
              fontFamily: theme.fonts?.serif,
              color: theme.text
            }}
          >
            Add New Clinic
          </h2>
          <div className="h-1 w-20 rounded-full" style={{ backgroundColor: theme.primary }} />
          <p className="text-sm mt-4 font-medium" style={{ color: theme.textSecondary }}>
            Fill in the details below to register a new clinic.
          </p>
        </div>

        {/* Form Section */}
        <form onSubmit={handleSubmit} className="px-8 md:px-12 py-8 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label
                className="block text-sm font-medium"
                style={{ color: theme.text }}
              >
                Clinic Name <span className="text-red-500">*</span>
              </label>
              <TransliteratedInput
                name="CLINIC_NAME"
                value={formData.CLINIC_NAME}
                onChangeText={(text) => setFormData({ ...formData, CLINIC_NAME: text })}
                className="w-full px-4 py-2.5 rounded-xl border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-opacity-50"
                style={{
                  backgroundColor: theme.inputBg,
                  borderColor: theme.border,
                  color: theme.text,
                }}
                placeholder="Enter clinic name"
                required
              />
            </div>

            <div className="space-y-2">
              <label
                className="block text-sm font-medium"
                style={{ color: theme.text }}
              >
                Clinic Phone No. <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="CLINIC_PHONE"
                value={formData.CLINIC_PHONE}
                onChange={handleChange}
                className="w-full px-4 py-2.5 rounded-xl border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-opacity-50"
                style={{
                  backgroundColor: theme.inputBg,
                  borderColor: theme.border,
                  color: theme.text,
                }}
                placeholder="Enter phone number"
                required
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <label
                className="block text-sm font-medium"
                style={{ color: theme.text }}
              >
                Clinic Address <span className="text-red-500">*</span>
              </label>
              <TransliteratedInput
                name="CLINIC_ADDRESS"
                value={formData.CLINIC_ADDRESS}
                onChangeText={(text) => setFormData({ ...formData, CLINIC_ADDRESS: text })}
                className="w-full px-4 py-2.5 rounded-xl border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-opacity-50"
                style={{
                  backgroundColor: theme.inputBg,
                  borderColor: theme.border,
                  color: theme.text,
                }}
                as="textarea"
                rows={2}
                placeholder="Enter clinic address"
                required
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <label
                className="block text-sm font-medium"
                style={{ color: theme.text }}
              >
                Clinic Logo
              </label>
              <input
                type="file"
                name="CLINIC_LOGO"
                onChange={handleChange}
                accept="image/*"
                className="w-full px-4 py-2 rounded-xl border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-opacity-50 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:opacity-90"
                style={{
                  backgroundColor: theme.inputBg,
                  borderColor: theme.border,
                  color: theme.text,
                }}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end items-center gap-4 pt-6 mt-6 border-t" style={{ borderColor: theme.border }}>
            <button
              type="button"
              onClick={() => navigate("/doctorslist")}
              className="px-6 py-2.5 rounded-full font-semibold transition-all duration-200 hover:scale-105 active:scale-95 text-sm"
              style={{
                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                color: theme.text,
                border: `1px solid ${theme.border}`
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-8 py-2.5 rounded-full font-bold text-white transition-all duration-200 hover:scale-105 active:scale-95 text-sm md:text-base shadow-lg"
              style={{
                backgroundImage: `linear-gradient(to right, ${theme.brownTwo}, ${theme.secondary}, ${theme.primary}, ${theme.brownTwo})`,
                backgroundSize: '200% auto',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundPosition = 'right center'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundPosition = 'left center'}
            >
              Add Clinic
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default AddClinic;
